import { useEffect, useRef, useState } from "react";
import { Maximize, Gauge } from "lucide-react";
import { streamUrl } from "@/lib/api";
import { CloudflarePlayer } from "@/components/site/CloudflarePlayer";

const SPEEDS = [0.5, 0.75, 1, 1.25, 1.5, 2];

function ytId(url) {
  const m = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([\w-]{11})/);
  return m ? m[1] : null;
}
function vimeoId(url) {
  const m = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
  return m ? m[1] : null;
}

export function VideoPlayer({ lessonId, source, videoType = "16:9", startAt = 0, onProgress, onEnded }) {
  const videoRef = useRef(null);
  const wrapRef = useRef(null);
  const [speed, setSpeed] = useState(1);
  const [showSpeed, setShowSpeed] = useState(false);
  const type = source?.type;
  // 9:16 vertical: fill mobile width, cap width on desktop, keep exact aspect ratio (never crop/stretch)
  const isVertical = videoType === "9:16" || type === "cloudflare";
  const aspect = isVertical
    ? "aspect-[9/16] w-full max-w-[420px] max-h-[80vh] mx-auto"
    : "aspect-video w-full";

  const isFile = type === "file";
  const url = isFile ? streamUrl(lessonId) : source?.url;
  const yt = url ? ytId(url) : null;
  const vm = url ? vimeoId(url) : null;

  useEffect(() => {
    const v = videoRef.current;
    if (v && startAt > 0) {
      const onLoaded = () => { try { v.currentTime = startAt; } catch (e) {} };
      v.addEventListener("loadedmetadata", onLoaded);
      return () => v.removeEventListener("loadedmetadata", onLoaded);
    }
  }, [startAt, url]);

  useEffect(() => {
    if (videoRef.current) videoRef.current.playbackRate = speed;
  }, [speed]);

  const goFull = () => {
    const el = wrapRef.current;
    if (el?.requestFullscreen) el.requestFullscreen();
    else if (videoRef.current?.webkitEnterFullscreen) videoRef.current.webkitEnterFullscreen();
  };

  // Cloudflare Stream: hls.js player with seamless signed-token refresh (9:16 optimized)
  if (type === "cloudflare") {
    return (
      <CloudflarePlayer
        lessonId={lessonId}
        source={source}
        startAt={startAt}
        onProgress={onProgress}
        onEnded={onEnded}
      />
    );
  }

  if (yt || vm) {
    const src = yt ? `https://www.youtube.com/embed/${yt}` : `https://player.vimeo.com/video/${vm}`;
    return (
      <div className={`relative overflow-hidden rounded-lg border border-white/10 bg-black ${aspect}`}>
        <iframe data-testid="video-iframe" title="lesson" src={src} allow="autoplay; fullscreen; picture-in-picture"
          allowFullScreen className="absolute inset-0 h-full w-full" />
      </div>
    );
  }

  return (
    <div ref={wrapRef} className={`relative overflow-hidden rounded-lg border border-white/10 bg-black ${aspect}`}>
      {url ? (
        <video
          ref={videoRef}
          data-testid="video-element"
          src={url}
          controls
          playsInline
          controlsList="nodownload"
          onContextMenu={(e) => e.preventDefault()}
          className="h-full w-full bg-black"
          onTimeUpdate={(e) => onProgress && onProgress(e.target.currentTime)}
          onEnded={() => onEnded && onEnded()}
        />
      ) : (
        <div className="flex h-full items-center justify-center text-zinc-500">No video available</div>
      )}
      {url && (
        <div className="absolute top-3 right-3 flex gap-2">
          <div className="relative">
            <button data-testid="speed-btn" onClick={() => setShowSpeed((s) => !s)}
              className="flex items-center gap-1 rounded bg-black/70 px-2 py-1 text-xs text-white border border-white/10">
              <Gauge className="h-3.5 w-3.5" /> {speed}x
            </button>
            {showSpeed && (
              <div className="absolute right-0 mt-1 rounded bg-black/90 border border-white/10 p-1">
                {SPEEDS.map((s) => (
                  <button key={s} onClick={() => { setSpeed(s); setShowSpeed(false); }}
                    className={`block w-16 rounded px-2 py-1 text-left text-xs ${s === speed ? "text-[#DCA846]" : "text-white"} hover:bg-white/10`}>
                    {s}x
                  </button>
                ))}
              </div>
            )}
          </div>
          <button data-testid="fullscreen-btn" onClick={goFull} className="rounded bg-black/70 p-1.5 text-white border border-white/10">
            <Maximize className="h-3.5 w-3.5" />
          </button>
        </div>
      )}
    </div>
  );
}
